import { Phone, Mail } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { HeroVideo } from "@/components/HeroVideo";
import { ServiceShowcase } from "@/components/ServiceShowcase";
import { ParallaxStatement } from "@/components/ParallaxStatement";
import { MediaShowcase } from "@/components/MediaShowcase";
import { StatsBand } from "@/components/StatsBand";
import { TrustBenefits } from "@/components/TrustBenefits";
import { ReviewsSection } from "@/components/ReviewsSection";
import { HotlineCards } from "@/components/HotlineCards";
import { ContactForm } from "@/components/ContactForm";
import { SectionDivider } from "@/components/SectionDivider";
import { BrandPatternLayer } from "@/components/BrandPatternLayer";
import { site } from "@/lib/siteConfig";
import { pageContent } from "@/cms/cmsModel";
import { useCmsDocument } from "@/cms/CmsContentProvider";

export default function Home() {
  useCmsDocument();
  const copy = pageContent.home;
  return (
    <>
      {/* ==================== FULL-WIDTH VIDEO HERO ==================== */}
      <HeroVideo />

      <SectionDivider />

      {/* ========================= SERVICES ========================= */}
      <section className="relative isolate overflow-hidden bg-[#F1F4F8]/90 py-16 lg:py-24">
        <BrandPatternLayer variant="corporate" composition="a" />
        <div className="relative z-10 az-container">
          <SectionHeading
            align="center"
            eyebrow={copy.servicesHeading.eyebrow}
            title={copy.servicesHeading.title}
            subtitle={copy.servicesHeading.subtitle}
            className="mb-12"
          />
          <ServiceShowcase />
        </div>
      </section>

      <SectionDivider />

      {/* ==================== PARALLAX STATEMENT ==================== */}
      <ParallaxStatement />

      <SectionDivider />

      {/* ====================== MEDIA SHOWCASE ===================== */}
      <section className="relative isolate overflow-hidden bg-[#F1F4F8]/90 py-16 lg:py-24">
        <BrandPatternLayer variant="corporate" composition="b" />
        <div className="relative z-10 az-container">
          <MediaShowcase />

          <div className="mt-16 lg:mt-24">
            <SectionHeading
              align="center"
              eyebrow={copy.benefitsHeading.eyebrow}
              title={copy.benefitsHeading.title}
              className="mb-12"
            />
            <TrustBenefits />
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ========================= STATS ========================= */}
      <StatsBand />

      <SectionDivider />

      {/* ====================== REVIEWS (home only) ====================== */}
      <ReviewsSection />

      <SectionDivider />

      {/* ====================== HOTLINE AREAS ====================== */}
      <HotlineCards />

      <SectionDivider />

      {/* ====================== CONTACT CTA + MINI FORM ====================== */}
      <section id="kontakt" className="relative isolate overflow-hidden bg-[#F1F4F8]/90 py-16 lg:py-24">
        <BrandPatternLayer variant="contact" composition="a" />
        <div className="relative z-10 az-container grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading
              eyebrow={copy.contact.eyebrow}
              title={copy.contact.title}
              subtitle={copy.contact.subtitle}
            />
            <div className="mt-8 space-y-4">
              <a
                href={`tel:${site.primaryPhoneTel}`}
                className="group flex items-center gap-4 rounded-2xl border border-[#D5DEE8] bg-white p-4 text-lg shadow-[var(--shadow-soft)] transition-shadow hover:shadow-[var(--shadow-card)]"
                data-testid="home-contact-phone-link"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#7A1516]/[0.08] text-[var(--az-icon-default)] transition-colors group-hover:text-[var(--az-icon-hover)] group-focus-visible:text-[var(--az-icon-hover)]">
                  <Phone size={20} />
                </span>
                <span>
                  <span className="block text-sm text-[#586576]">{copy.contact.phoneLabel}</span>
                  <span className="font-semibold text-[#193D6D]">{site.primaryPhone}</span>
                </span>
              </a>
              <a
                href={`mailto:${site.email}`}
                className="group flex items-center gap-4 rounded-2xl border border-[#D5DEE8] bg-white p-4 text-lg shadow-[var(--shadow-soft)] transition-shadow hover:shadow-[var(--shadow-card)]"
                data-testid="home-contact-email-link"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#7A1516]/[0.08] text-[var(--az-icon-default)] transition-colors group-hover:text-[var(--az-icon-hover)] group-focus-visible:text-[var(--az-icon-hover)]">
                  <Mail size={20} />
                </span>
                <span>
                  <span className="block text-sm text-[#586576]">{copy.contact.emailLabel}</span>
                  <span className="font-semibold text-[#193D6D]">{site.email}</span>
                </span>
              </a>
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-[#D5DEE8] bg-white p-6 shadow-[var(--shadow-card)] sm:p-8">
              <h3 className="font-heading text-2xl font-semibold text-[#193D6D]">
                {copy.contact.formTitle}
              </h3>
              <p className="mt-1 mb-5 text-[#586576]">{copy.contact.formSubtitle}</p>
              <ContactForm
                sourcePage="startseite"
                testidPrefix="home-contact-form"
                submitLabel={copy.contact.submitLabel}
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
